import Fees from 'client/js/models/fees'
import FeeCalculator from 'common/models/fee-calculator'
import AsyncTools from 'common/helpers/async-tools'
import Calendar from 'client/js/models/calendar'

import StudentPreferences from 'client/js/models/student-preferences'

function getNewStateObj() {
   return {
      preferences: {
         studentType: null,
         placeOfResidence: null,
         studyOption: null,
         existingStudent: null,
         commencementYear: null,
         startYear: null,
         teachingPeriod: null
      },
      teachingPeriods: {},
      fees: {
         commencementYear: '',
         courseType: '',
         feeType: '',
         studentType: '',
         studentTypeCode: '',
         calculator: null
      }
   }
}

const getters = {
   // returns the available teaching periods
   getTeachingPeriods: state => state.teachingPeriods,

   // returns the selected study preferences
   getSelectedStudyPreferences: state => state.preferences,

   // returns the fee code associated with the user
   getFeesPreferences: state => state.fees,

   // Checks if fees are properly loaded yet - by checking if the FeeCalculator class is in place (it isn't after persistence restore)
   getFeesLoaded: state => state.fees.calculator instanceof FeeCalculator,

   // Check selected preferences and return true if an International Student has picked a Study Option other than 12 points full time
   isInternationalPartTime: state => state.preferences.studentType == 'International' && state.preferences.studyOption && state.preferences.studyOption != 'Full Time - 12'
}

const mutations = {

   // returns the available teaching periods
   setTeachingPeriods(state, teachingPeriods) {
      state.teachingPeriods = teachingPeriods;
   },

   // set selected study preferences
   setStudyPreferences(state, preferences) {
      state.preferences = preferences;
   },

   // set the fee data
   setFeeData(state, feeData) {
      state.fees = feeData;
   },

   resetState: (state) => {
      Object.assign(state, getNewStateObj())
   }
}

const actions = {

   async validateAndLoadPreferences({commit, getters, dispatch, rootGetters}, {selectedPreferences}) {
      let loadedCourse = rootGetters["courses/getSelectedCourse"]

      //load up preferences,and validate them
      let loadedPreferences = await new StudentPreferences()
      await loadedPreferences.setCourse(loadedCourse)
      await loadedPreferences.setData(selectedPreferences)

      //populate the teaching periods for the selected years (if possible)
      await dispatch('populateTeachingPeriods');

      let errors = await loadedPreferences.validate()
      commit('setStudyPreferences', loadedPreferences);
      if (errors) throw new Error('error validating student preferences')
   },

   //populate the teaching periods based on offering years
   async populateTeachingPeriods({commit, getters }) {
      const teachingPeriods = getters['getTeachingPeriods'];

      const offeringYears = []
      const maxYear = new Date().getFullYear() + StudentPreferences.forwardPlanningAllowedInYears // max date is next year

      for (let i = new Date().getFullYear(); i <= maxYear; i++) {
        offeringYears.push(i)
      }

      await AsyncTools.eachLimit(offeringYears, async (year) => {
         try {
            //only try to get years we dont already have.
            if (teachingPeriods[year] == null)
               teachingPeriods[year] = await Calendar.getTeachingPeriods(year)
         } catch (err) {
            //TODO: perhaps log it somewhere
            console.log('Error getting Teaching Periods for',year,err)
            throw (err)
         }
      });
      commit('setTeachingPeriods', teachingPeriods)
   },

   //returns the commencement years based on the selected course and student type.
   async populateCommencementYears({commit, getters }, { selectedCourse, studentType }) {
      if (selectedCourse && studentType) {

         // convert study preference to fee input model
         let feeModel = StudentPreferences.mapToFeeModel(
            studentType,
            selectedCourse.classification.marketedCourseType,
            selectedCourse.commonwealthSupported,
            null
         )

         // const possibleStudentTypes = []
         const possibleStudentTypes = await Fees.getPotentialStudentTypes(
            feeModel.studentType,
            feeModel.feeType,
            feeModel.courseType
         )
         return possibleStudentTypes.map(x=> x.commencementYear)
                  .reduce((acc,curr)=> {
                     if (!acc.find(x => x == curr)) {
                        acc.push(curr)
                     }
                     return acc
                  },[])
                  .map(x => {
                     return  {label:x, value:x}
                  })
      }
      return []
   },

   // fetch fees from fees service based on the provided course
   // and study preferences saved in state
   async fetchSelectedCourseFees({ commit, getters, rootState, rootGetters }) {

      const course = rootGetters['courses/getSelectedCourse'];
      const preferences = getters['getSelectedStudyPreferences'];

      let feeCalculationYear = null;
      if (preferences.existingStudent == 'Yes')
      feeCalculationYear = preferences.commencementYear

      // convert study preference to fee input model
      let feeModel = StudentPreferences.mapToFeeModel(
         preferences.studentType,
         course.classification.marketedCourseType,
         course.commonwealthSupported,
         feeCalculationYear
      );

      try {
         // get the fee code from the fee service
         feeModel = await Fees.getFeeCode(
            feeModel.studentType,
            feeModel.feeType,
            feeModel.commencementYear,
            feeModel.courseType
         )

         // load the fee data based on the fee code
         let feeData = {}
         if (feeModel.studentTypeCode) {
            feeData = await Fees.getFees(feeModel.studentTypeCode)
         }

         // save the fee model and data to state
         let fees = feeData && feeData.fees ? feeData.fees : []
         commit('setFeeData', {
            ...feeModel,
            calculator: new FeeCalculator(course, fees)
         })
      } catch (err) {
         // TODO: handle error
         commit('setFeeData', {
            ...feeModel,
            studentTypeCode: '',
            calculator: null
         })
         throw (err)
      }
   },

}

export default {
   namespaced: true,
   state: getNewStateObj(),
   getters,
   actions,
   mutations,
   getNewStateObj
}
